import React, { useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStore, faArrowRight, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

// Default gradient backgrounds when no logo is present
const gradients = [
    "from-blue-400 to-blue-600",
    "from-purple-400 to-purple-600",
    "from-green-400 to-emerald-600",
    "from-orange-400 to-orange-600",
    "from-pink-400 to-rose-600",
    "from-teal-400 to-cyan-600",
];

const getGradient = (id) => gradients[id % gradients.length];

function StoreCard({ store, index }) {
    const [imgError, setImgError] = useState(false);
    const hasLogo = store.logo && !imgError;
    const gradient = getGradient(store.id ?? index);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: Math.min(index * 0.05, 0.5) }}
            whileHover={{ y: -4 }}
            className="h-full"
        >
            <Link to={`/${store.slug}`} className="no-underline h-full flex">
                <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden w-full flex flex-col border border-gray-100">

                    {/* Banner / Header area */}
                    <div className={`relative h-24 bg-gradient-to-br ${gradient} flex-shrink-0`}>
                        {/* Subtle pattern overlay */}
                        <div className="absolute inset-0 opacity-10"
                            style={{ backgroundImage: "radial-gradient(circle at 20% 80%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)", backgroundSize: "30px 30px" }}
                        />

                        {/* Verified badge */}
                        <div className="absolute top-2 right-2 bg-white/20 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-[9px]" />
                            <span>Verificado</span>
                        </div>

                        {/* Logo circle — overlaps banner and card body */}
                        <div className="absolute -bottom-8 left-4">
                            <div className="w-16 h-16 rounded-full ring-4 ring-white shadow-md overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                                {hasLogo ? (
                                    <img
                                        src={store.logo}
                                        alt={store.name}
                                        className="w-full h-full object-cover"
                                        onError={() => setImgError(true)}
                                    />
                                ) : (
                                    <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                                        <FontAwesomeIcon icon={faStore} className="text-white text-xl" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Card body */}
                    <div className="pt-10 px-4 pb-4 flex flex-col flex-1">
                        {/* Name */}
                        <p className="font-bold text-gray-800 text-sm leading-tight truncate mb-1">
                            {store.name} {store.last_name}
                        </p>

                        {/* Description */}
                        {store.description ? (
                            <p className="text-xs text-gray-400 leading-relaxed line-clamp-2 mb-3">
                                {store.description}
                            </p>
                        ) : (
                            <p className="text-xs text-gray-300 italic leading-relaxed mb-3">
                                Sin descripción disponible
                            </p>
                        )}

                        {/* Footer CTA */}
                        <div className="mt-auto flex items-center justify-between">
                            <span className="text-[11px] text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">
                                Ver tienda
                            </span>
                            <div className="w-7 h-7 flex items-center justify-center bg-ecoprimary1 rounded-full text-white">
                                <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}

const StoreList = ({ array_store, loadMore, current_page, last_page }) => {
    return (
        <div className="container pb-12">
            {/* Section header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-1">
                        Todos los emprendedores
                    </h2>
                    <div className="h-1 w-12 rounded-full" style={{ background: "linear-gradient(90deg, #0049a8, #51A4A9)" }} />
                </div>
                <span className="text-sm text-gray-400 bg-gray-50 px-3 py-1 rounded-full border border-gray-100">
                    {array_store.length} tiendas
                </span>
            </div>

            <InfiniteScroll
                dataLength={array_store.length}
                next={loadMore}
                hasMore={current_page < last_page}
                loader={
                    <div className="flex justify-center py-6">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                            <div className="w-4 h-4 border-2 border-ecoprimary1 border-t-transparent rounded-full animate-spin" />
                            Cargando más tiendas...
                        </div>
                    </div>
                }
            >
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                    {array_store.map((store, index) => (
                        <StoreCard key={store.id} store={store} index={index} />
                    ))}
                </div>

                {current_page >= last_page && array_store.length > 0 && (
                    <p className="text-center py-8 text-gray-400 text-sm">
                        Has visto todos los emprendedores disponibles.
                    </p>
                )}
            </InfiniteScroll>
        </div>
    );
};

const mapStateToProps = state => ({
    current_page: state.Store.current_page,
    last_page: state.Store.last_page,
});

export default connect(mapStateToProps, {})(StoreList);
